import { Component, OnInit } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfigService } from 'src/app/config/shared/config.service';
import { BetaFeatureToggleEnum } from 'src/app/config/shared/constants/beta-feature-toggle.enum';
import { FormSearch } from 'src/app/forms/shared/model/form-search.model';
import { FormTemplate } from 'src/app/forms/shared/model/form-template.model';
import { FormBuilderSettings } from 'src/app/forms/shared/model/settings/form-builder-settings.model';
import { DatagridService, NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { FormBuilderDto } from '../shared/dto/form-builder.dto';
import { FormSettingsDto } from '../shared/dto/form-settings.dto';
import { FormV2TypeEnum } from '../shared/enum/form-v2-type.enum';
import { FormBuilderService } from '../shared/service/form-builder.service';

@Component({
  selector: 'wl-form-list',
  templateUrl: './form-list.component.html',
  styleUrls: ['./form-list.component.less'],
    standalone: false
})
export class FormListComponent extends LazySelectableTableComponent<FormSearch, FormTemplate> implements OnInit {
  newFormActionItems: MenuItem[];
  rowMenuItems: { [key: string]: MenuItem[] } = {};
  showInactive: boolean = false;
  constructor(
    datagridService: DatagridService,
    private _formBuilderService: FormBuilderService,
    private _translationService: TranslationService,
    private _navigationService: NavigationService,
    private _configService: ConfigService,
    private _notificationService: NotificationService,
    private _confirmationService: ConfirmationService
  ) {
    super('Id', datagridService);

    this.searchParams = new FormSearch();
  }

  async ngOnInit(): Promise<void> {
    this.newFormActionItems = [
      {
        label: await this._translationService.getCapitalizeFirstCharacter('form-builder.form-list.join-form'),
        command: () => this._navigationService.navigateToFormBuilderCreateTemplate(),
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('form-builder.form-list.donation-form'),
        command: () => this._navigationService.navigateToNewForm(FormV2TypeEnum.Donation),
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('form-builder.form-list.renewal-form'),
        command: () => this._navigationService.navigateToNewForm(FormV2TypeEnum.Renewal),
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('form-builder.form-list.prospect-form'),
        command: () => this._navigationService.navigateToNewForm(FormV2TypeEnum.Prospect),
      },
      {
        label: await this._translationService.getCapitalizeFirstCharacter('form-builder.form-list.general-form'),
        command: () => this._navigationService.navigateToNewForm(FormV2TypeEnum.GeneralSurvey),
      },
    ];
  }

  setRelationActionItems(formTemplate: FormTemplate) {
    if (!this.rowMenuItems[formTemplate.Id]) {
      this.rowMenuItems[formTemplate.Id] = [];
    }

    this.rowMenuItems[formTemplate.Id] = [
      {
        label: this._translationService.getInstantTitlecase('common.copy'),
        command: () => {
          this.copyForm(formTemplate);
        },
      },
      /*{
        label: this._translationService.getInstantTitlecase('common.delete'),
        command: () => {
          this.deleteForm(formTemplate);
        },
      },*/
      {
        visible: !formTemplate.Inactive,
        label: this._translationService.getInstantTitlecase('common.deactivate'),
        command: () => {
          this.toggleInactive(formTemplate, true);
        },
      },
      {
        visible: formTemplate.Inactive,
        label: this._translationService.getInstantTitlecase('common.reactivate'),
        command: () => {
          this.toggleInactive(formTemplate, false);
        },
      },
    ];
  }

  copyForm(formTemplate: FormTemplate) {
    this._formBuilderService.get(formTemplate.Id).then((form) => {
      delete form.Id;
      let jsonObj = JSON.parse(form.Dto);
      delete jsonObj.Id;
      form.Dto = JSON.stringify(jsonObj);
      this._formBuilderService
        .saveForm(form)
        .then((formId) => {
          this._navigationService.navigateToFormEdit(formId, form.FormType);
        })
        .catch((err) => {
          this._notificationService.httpError(err);
        });
    });
  }

  deleteForm(formTemplate: FormTemplate) {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('form-builder.form-list.confirm-delete-message'),
      key: 'deleteFormTemplate',
      accept: () => {
        this._formBuilderService
          .delete(formTemplate.Id)
          .then(() => {
            this._translationService.get('common.success').then((message) => {
              this._notificationService.success(message);
              this.search();
            });
          })
          .catch((err) => {
            this._notificationService.httpError(err);
          });
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  toggleInactive(formTemplate: FormTemplate, bool: boolean) {
    this._formBuilderService.toggleInactive(formTemplate.Id, bool).then(() => {
      this.search();
    });
  }

  fetchResults(): Promise<PagedResultGeneric<FormTemplate>> {
    this.searchParams.ShowInactive = this.showInactive;
    this.rowMenuItems = {};
    return this._formBuilderService.searchForms(this.searchParams).then((results) => {
      results.Result.forEach((formTemplate) => {
        this.setRelationActionItems(formTemplate);
      });
      return results;
    });
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  getEditLink(formType: FormV2TypeEnum, formId: number): string {
    switch (formType) {
      case FormV2TypeEnum.Donation:
        return `/form-builder/donation/${formId}/edit`;
        break;
      case FormV2TypeEnum.GeneralSurvey:
        return `/form-builder/general/${formId}/edit`;
        break;
      case FormV2TypeEnum.Join:
        return `/forms/formbuilder/${formId}`;
        break;
      case FormV2TypeEnum.Prospect:
        return `/form-builder/prospect/${formId}/edit`;
        break;
      case FormV2TypeEnum.Renewal:
        return `/form-builder/renewal/${formId}/edit`;
        break;
    }
  }

  getFormTypeDisplayName(formType: FormV2TypeEnum): string {
    return `${FormV2TypeEnum[formType].toString().replace('Survey', '')}`;
  }
}
